import { Button, Modal, Title } from "@mantine/core";
import { useState } from "react";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { DeleteConfirmationModal } from "../../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { ORIGIN } from "../CreateEdaVersionModal/CreateEdaVersionModal.types";
import type { Eda } from "../EdaTable/Mbk";
import { useMbksMutation } from "../EdaVisualization/hooks/useMbks";
import "./ViewEdaModal.scss";

type ViewEdaModal = {
  isOpen: boolean;
  siteId: number;
  onToggle: () => void;
  eda: Eda;
  onEditEda: () => void;
};

export function ViewEdaModal({
  isOpen,
  onEditEda,
  siteId,
  onToggle,
  eda
}: ViewEdaModal) {
  const { showErrorToast } = useToast();
  const { mutateAsync: deleteEDA, isPending } = useMbksMutation(
    siteId,
    "DELETE"
  );
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] =
    useState(false);

  async function handleDeleteEda() {
    setDeleteConfirmationIsOpen(false);

    try {
      await deleteEDA([eda]);
      onToggle();
    } catch (error) {
      showErrorToast(error);
    }
  }

  const medium = eda.versions[0]?.computation?.medium;

  return (
    <Modal opened={isOpen} title="Energiedatenerfassung" onClose={onToggle}>
      <div>
        <div className="viewEdaModal-property">
          <Title order={6}>ID</Title>
          <span className="viewEdaModal-value">{eda.versions[0].publicId}</span>
        </div>
        <div className="viewEdaModal-property">
          <Title order={6}>Art der Energiemenge</Title>
          <span className="viewEdaModal-value">{eda.identity.type}</span>
        </div>
        <div className="viewEdaModal-property">
          <Title order={6}>Referenzen</Title>
          <span className="viewEdaModal-value">
            {Object.keys(eda.identity)
              .filter((i) => i !== "type")
              .join(", ")}
          </span>
        </div>

        <div className="viewEdaModal-property">
          <Title order={6}>Herkunft</Title>
          <span className="viewEdaModal-value">
            {ORIGIN[eda.versions[0].origin]}
          </span>
        </div>
        <div className="viewEdaModal-property">
          <Title order={6}>Ergänzung</Title>
          <span className="viewEdaModal-value">
            {eda.versions[0].customLabel}
          </span>
        </div>
        <div className="viewEdaModal-property">
          <Title order={6}>Gültigkeitzeitraum</Title>
          <span className="viewEdaModal-value">
            {eda.versions[0].firstDate} - {eda.versions[0].lastDate}
          </span>
        </div>
        {medium && (
          <div className="viewEdaModal-property">
            <Title order={6}>Medium</Title>
            <span className="viewEdaModal-value">Strom</span>
          </div>
        )}
        {eda?.versions[0]?.computation?.formulaStr && (
          <div className="viewEdaModal-property">
            <Title order={6}>Details</Title>
            <span className="viewEdaModal-value">
              {eda.versions[0].computation.formulaStr}
            </span>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button
          color="error"
          loading={isPending}
          onClick={() => setDeleteConfirmationIsOpen(true)}
        >
          Löschen
        </Button>
        <Button color="primary" onClick={onEditEda}>
          Bearbeiten
        </Button>
        <Button variant="default" onClick={onToggle}>
          Schließen
        </Button>
        <DeleteConfirmationModal
          name={`Energiedatenerfassung ${eda.versions[0].publicId}`}
          opened={deleteConfirmationIsOpen}
          toggleModal={() => setDeleteConfirmationIsOpen(false)}
          onConfirm={() => handleDeleteEda()}
        />
      </ModalFooter>
    </Modal>
  );
}
